import Navbar from "@/components/feature/Navbar";
import HeroSection from "./components/HeroSection";
import CategoryScroll from "./components/CategoryScroll";
import ArticleGrid from "./components/ArticleGrid";
import TrustSection from "./components/TrustSection";
import TestimonialSection from "./components/TestimonialSection";
import CTASection from "./components/CTASection";
import FooterSection from "./components/FooterSection";
import { categories } from "@/mocks/categories";

export default function Home() {
  return (
    <main>
      <Navbar />
      <HeroSection />

      <section className="py-14 md:py-20 bg-background-50">
        <div className="px-6 md:px-10">
          <div className="max-w-3xl">
            <span className="inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full bg-primary-100 text-primary-700 whitespace-nowrap">
              What Is PandabuySpreadsheet?
            </span>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground-900 mt-4">
              Your Shortcut to Curated Fashion Finds
            </h2>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
              <strong className="text-foreground-900">PandabuySpreadsheet</strong> is a curated collection of shopping agent product lists that allow users to easily discover fashion, sneakers, accessories, and tech items at affordable prices through Pandabuy agents. This platform organizes thousands of hidden finds into structured spreadsheets for faster browsing and better decision-making. Instead of scrolling through endless forum threads and unverified links, you get a single organized directory of community-tested products — all in one place, updated daily.
            </p>

            <h3 className="font-display text-xl font-semibold text-foreground-900 mt-10 mb-4">
              Why Use Our PandabuySpreadsheet?
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                "Save 90% browsing time with organized categories",
                "Discover trending replica & budget fashion items",
                "Access structured verified finds from real buyers",
                "Avoid low-quality sellers with community ratings",
                "Updated daily with fresh spreadsheet data",
              ].map((item) => (
                <div key={item} className="flex items-start gap-2">
                  <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-check-line text-xs"></i>
                  </span>
                  <span className="text-sm text-foreground-600">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-background-100">
        <div className="px-6 md:px-10">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <span className="inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full bg-accent-100 text-accent-700 whitespace-nowrap">
              How It Works
            </span>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground-900 mt-4">
              From Browse to Buy in 4 Simple Steps
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { step: "01", title: "Browse Spreadsheet", desc: "Explore our PandabuySpreadsheet directory across 14 curated categories" },
              { step: "02", title: "Copy Item Link", desc: "Find something you like and copy the product link from the spreadsheet" },
              { step: "03", title: "Paste into Agent", desc: "Paste the link into your Pandabuy shopping agent platform" },
              { step: "04", title: "Order & Ship", desc: "Complete your order and get it shipped directly to your doorstep" },
            ].map((s) => (
              <div key={s.step} className="bg-background-50 rounded-lg p-6">
                <span className="font-mono text-3xl font-bold text-primary-300">{s.step}</span>
                <h4 className="text-sm font-semibold text-foreground-900 mt-3">{s.title}</h4>
                <p className="text-xs text-foreground-500 mt-1">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CategoryScroll />

      <ArticleGrid />

      <TrustSection />

      <TestimonialSection />

      <CTASection />

      <section className="py-10 md:py-14 bg-accent-50">
        <div className="px-6 md:px-10 max-w-3xl mx-auto text-center">
          <span className="inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full bg-white text-accent-700 whitespace-nowrap">
            Safety Notice
          </span>
          <p className="mt-4 text-sm text-foreground-500 leading-relaxed">
            We do not sell products directly. All links point to the main website. Users are responsible for customs compliance and local laws. Always verify product details before purchasing.
          </p>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-background-50">
        <div className="px-6 md:px-10">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
            All <span className="text-primary-600">Categories</span>
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-4 max-w-6xl mx-auto">
            {categories.map((cat) => (
              <a
                key={cat.id}
                href={cat.link}
                target="_blank"
                rel="noopener nofollow"
                className="group flex flex-col items-center gap-2 p-3 rounded-xl bg-background-100 hover:bg-primary-50 transition-colors"
              >
                <div className="w-full aspect-square rounded-lg overflow-hidden">
                  <img
                    src={cat.gridImageUrl}
                    alt={`${cat.name} category`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                    loading="lazy"
                  />
                </div>
                <span className="text-xs font-semibold text-foreground-700 group-hover:text-primary-700 whitespace-nowrap text-center leading-tight">
                  {cat.name}
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      <FooterSection />
    </main>
  );
}